import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createPublisherClient } from './api';
import type { PublisherListFilter, PublisherMutationPayload } from './model';

export const publishersQueryKey = ['supply', 'publishers'] as const;
export const publishersListsQueryKey = [...publishersQueryKey, 'list'] as const;

export function usePublisherClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createPublisherClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function usePublishersQuery(filter: PublisherListFilter) {
  const client = usePublisherClient();
  return useQuery({
    queryKey: [...publishersListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function usePublisherQuery(id: string | undefined) {
  const client = usePublisherClient();
  return useQuery({
    queryKey: [...publishersQueryKey, 'detail', id],
    queryFn: () => client.get(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function usePublisherMutations() {
  const client = usePublisherClient();
  const queryClient = useQueryClient();
  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: publishersListsQueryKey });
  return {
    create: useMutation({ mutationFn: client.create, onSuccess: invalidateLists }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: PublisherMutationPayload }) =>
        client.update(id, payload),
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: publishersListsQueryKey,
          refetchType: 'none',
        }),
    }),
    updateCosts: useMutation({
      mutationFn: ({
        id,
        payload,
      }: {
        id: string;
        payload: Pick<PublisherMutationPayload, 'allProductsCost' | 'saasCost'>;
      }) => client.updateCosts(id, payload),
      onSuccess: invalidateLists,
    }),
  };
}

export function usePublisherStatusCountsQuery(searchTerm: string) {
  const client = usePublisherClient();
  const search = searchTerm.trim();
  return useQuery({
    queryKey: [...publishersListsQueryKey, { statusCounts: true }, search],
    queryFn: () => client.getStatusCounts(search),
  });
}
